import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  Archive,
  Ban,
  BarChart3,
  BellOff,
  ChevronDown,
  ExternalLink,
  Lightbulb,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Paperclip,
  Plus,
  Radio,
  Search,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import type {
  Broadcast,
  BroadcastDelivery,
  ConversationMessage,
  Proposal,
  ProposalRevision,
  PublicUser,
} from "@serezha168/shared";
import { PROPOSAL_STATUS_LABELS, saratovLocalToIso } from "@serezha168/shared";
import { api } from "../api";
import { useAsync, useDebouncedEffect } from "../hooks";
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Modal,
  Spinner,
  Toast,
  formatDate,
} from "../components/Ui";

export function AdminLayout({ user }: { user: PublicUser }) {
  const [mobileNav, setMobileNav] = useState(false);
  const links = [
    ["/admin", "Обзор", BarChart3],
    ["/admin/messages", "Сообщения", MessageSquare],
    ["/admin/ideas", "Идеи", Lightbulb],
    ["/admin/users", "Пользователи", Users],
    ["/admin/broadcasts", "Рассылки", Radio],
  ] as const;
  const logout = async () => {
    try {
      await api.logout();
    } finally {
      window.sessionStorage.removeItem("s168_admin_session");
      window.localStorage.removeItem("s168_session");
      window.location.assign("/admin");
    }
  };
  return (
    <div className="admin-shell">
      <aside
        className={`admin-sidebar ${mobileNav ? "admin-sidebar--open" : ""}`}
      >
        <div className="admin-sidebar__brand">
          <span className="brand__mark">S</span>
          <div>
            <strong>SEREZHA168</strong>
            <small>Админ-панель</small>
          </div>
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileNav(false)}
          >
            <X />
          </button>
        </div>
        <nav>
          {links.map(([to, label, Icon]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/admin"}
              onClick={() => setMobileNav(false)}
            >
              <Icon size={19} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="admin-profile">
          <Avatar name={user.firstName} src={user.photoUrl} size={38} />
          <div>
            <strong>{user.firstName}</strong>
            <small>Владелец</small>
          </div>
          {!window.Telegram?.WebApp.initData ? (
            <button className="icon-button" title="Выйти" onClick={logout}>
              <LogOut size={18} />
            </button>
          ) : null}
        </div>
      </aside>
      <div className="admin-content">
        <header className="admin-topbar">
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileNav(true)}
          >
            <Menu />
          </button>
          <div>
            <strong>Панель управления</strong>
            <span>Саратовское время · UTC+4</span>
          </div>
        </header>
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function DashboardAdminPage() {
  const state = useAsync(() => api.dashboard(), []);
  if (state.loading) return <Spinner />;
  const stats = state.data?.stats;
  const items = [
    ["Пользователи", stats?.users ?? 0, Users, "blue"],
    ["Активны 30 дней", stats?.activeUsers ?? 0, BarChart3, "green"],
    ["Непрочитанные", stats?.unreadMessages ?? 0, Mail, "orange"],
    ["Новые идеи", stats?.newProposals ?? 0, Lightbulb, "violet"],
    ["Активные рассылки", stats?.scheduledBroadcasts ?? 0, Radio, "red"],
  ] as const;
  return (
    <div className="admin-page">
      <div className="admin-heading">
        <div>
          <p className="eyebrow">Сегодня</p>
          <h1>Обзор</h1>
          <p>Самое важное по боту и аудитории.</p>
        </div>
      </div>
      {state.error ? <p className="form-error">{state.error}</p> : null}
      <div className="stats-grid">
        {items.map(([label, value, Icon, color]) => (
          <Card key={label} className="stat-card">
            <div className={`stat-icon ${color}`}>
              <Icon />
            </div>
            <strong>{value.toLocaleString("ru-RU")}</strong>
            <span>{label}</span>
          </Card>
        ))}
      </div>
      <div className="admin-grid">
        <Card>
          <h2>Быстрые действия</h2>
          <div className="quick-actions">
            <NavLink to="/admin/messages">
              <MessageSquare />
              Ответить на сообщения
            </NavLink>
            <NavLink to="/admin/ideas">
              <Lightbulb />
              Разобрать идеи
            </NavLink>
            <NavLink to="/admin/broadcasts">
              <Radio />
              Создать рассылку
            </NavLink>
          </div>
        </Card>
        <Card>
          <h2>{stats?.testMode ? "Закрытый тест" : "Публичный режим"}</h2>
          {stats?.testMode ? (
            <p>
              Бот доступен только владельцу и разрешённым тестовым аккаунтам.
              Массовая отправка другим пользователям заблокирована на сервере.
            </p>
          ) : (
            <p>
              Бот открыт для всех пользователей. Рассылки могут отправляться
              всей согласившейся аудитории — проверяйте число получателей перед
              запуском.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

export function ConversationsAdminPage() {
  const [query, setQuery] = useState("");
  const [archived, setArchived] = useState(false);
  const conversations = useAsync(
    () => api.conversations(query, archived),
    [query, archived],
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const messages = useAsync(
    () =>
      selectedId
        ? api.messages(selectedId)
        : Promise.resolve({ messages: [], hasMore: false }),
    [selectedId],
  );
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState<ConversationMessage | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [sendMediaKind, setSendMediaKind] = useState<
    "auto" | "photo" | "video" | "voice" | "video_note" | "document"
  >("auto");
  const [mediaUrl, setMediaUrl] = useState("");
  const [toast, setToast] = useState("");
  const [mediaKind, setMediaKind] =
    useState<ConversationMessage["kind"]>("document");
  const bottom = useRef<HTMLDivElement>(null);
  const selected = conversations.data?.conversations.find(
    (item) => item.user.id === selectedId,
  )?.user;
  useEffect(
    () => bottom.current?.scrollIntoView({ behavior: "smooth" }),
    [messages.data],
  );
  useDebouncedEffect(
    () => {
      if (selectedId)
        void (text
          ? api.saveDraft("reply", String(selectedId), { text })
          : api.clearDraft("reply", String(selectedId)));
    },
    800,
    [text, selectedId],
  );
  useEffect(() => {
    if (selectedId)
      void api
        .loadDraft("reply", String(selectedId))
        .then((value) => setText(String(value.draft?.text ?? "")));
  }, [selectedId]);
  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedId || (!text.trim() && !attachment)) return;
    setSending(true);
    try {
      let attachmentId: string | undefined;
      let kind:
        "text" | "photo" | "video" | "voice" | "video_note" | "document" =
        "text";
      if (attachment) {
        attachmentId = (await api.upload(attachment)).id;
        const inferredKind = attachment.type.startsWith("image/")
          ? "photo"
          : attachment.type.startsWith("video/")
            ? "video"
            : attachment.type.startsWith("audio/")
              ? "voice"
              : "document";
        kind = sendMediaKind === "auto" ? inferredKind : sendMediaKind;
      }
      await api.sendMessage(selectedId, {
        text,
        kind,
        attachmentId,
        replyToMessageId: replyTo?.id,
      });
      setText("");
      setAttachment(null);
      setSendMediaKind("auto");
      setReplyTo(null);
      await api.clearDraft("reply", String(selectedId));
      await messages.reload();
      await conversations.reload();
    } catch (caught) {
      setToast(
        caught instanceof Error ? caught.message : "Не удалось отправить ответ",
      );
    } finally {
      setSending(false);
    }
  };
  const openMedia = async (message: ConversationMessage) => {
    if (!message.fileId) return;
    try {
      const blob = await api.messageFile(message.id);
      if (mediaUrl) URL.revokeObjectURL(mediaUrl);
      setMediaKind(message.kind);
      setMediaUrl(URL.createObjectURL(blob));
    } catch (caught) {
      setToast(
        caught instanceof Error ? caught.message : "Не удалось открыть вложение",
      );
    }
  };
  const loadEarlier = async () => {
    if (
      !selectedId ||
      !messages.data?.hasMore ||
      !messages.data.nextBeforeCreated ||
      !messages.data.nextBeforeId
    )
      return;
    try {
      const earlier = await api.messages(
        selectedId,
        messages.data.nextBeforeCreated,
        messages.data.nextBeforeId,
      );
      messages.setData({
        ...earlier,
        messages: [...earlier.messages, ...messages.data.messages],
      });
    } catch (caught) {
      setToast(
        caught instanceof Error ? caught.message : "Не удалось загрузить историю",
      );
    }
  };
  return (
    <div className="inbox">
      <aside
        className={`inbox-list ${selectedId ? "inbox-list--hidden-mobile" : ""}`}
      >
        <div className="inbox-list__header">
          <div className="inbox-title">
            <h1>Сообщения</h1>
            <button
              className={archived ? "active" : ""}
              title="Архив"
              onClick={() => {
                setArchived(!archived);
                setSelectedId(null);
              }}
            >
              <Archive size={18} />
            </button>
          </div>
          <label className="search">
            <Search />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Поиск"
            />
          </label>
        </div>
        {conversations.loading ? (
          <Spinner />
        ) : (
          conversations.data?.conversations.map((item) => (
            <button
              key={item.user.id}
              className={`conversation-item ${selectedId === item.user.id ? "active" : ""}`}
              onClick={() => setSelectedId(item.user.id)}
            >
              <Avatar name={item.user.firstName} src={item.user.photoUrl} />
              <div>
                <strong>
                  {item.user.firstName} {item.user.lastName}
                </strong>
                <span>{item.lastMessagePreview || "Нет сообщений"}</span>
              </div>
              {item.user.unreadCount ? <b>{item.user.unreadCount}</b> : null}
            </button>
          ))
        )}
      </aside>
      <section
        className={`chat-panel ${!selectedId ? "chat-panel--hidden-mobile" : ""}`}
      >
        {selected ? (
          <>
            <header className="chat-header">
              <button
                className="icon-button mobile-only"
                onClick={() => setSelectedId(null)}
              >
                <ChevronDown className="rotate-90" />
              </button>
              <Avatar name={selected.firstName} src={selected.photoUrl} />
              <div>
                <strong>
                  {selected.firstName} {selected.lastName}
                </strong>
                <span>
                  {selected.username
                    ? `@${selected.username}`
                    : `ID ${selected.id}`}
                </span>
              </div>
              <a
                className="icon-button"
                href={
                  selected.username
                    ? `https://t.me/${selected.username}`
                    : `tg://user?id=${selected.id}`
                }
              >
                <ExternalLink />
              </a>
            </header>
            <div className="message-stream">
              {messages.data?.hasMore ? (
                <Button variant="secondary" onClick={loadEarlier}>
                  Показать более ранние сообщения
                </Button>
              ) : null}
              {messages.loading ? (
                <Spinner />
              ) : (
                messages.data?.messages.map((message) => (
                  <button
                    key={message.id}
                    className={`message-bubble ${message.direction}`}
                    onClick={() =>
                      message.fileId ? openMedia(message) : setReplyTo(message)
                    }
                  >
                    {message.replyPreview ? (
                      <blockquote>{message.replyPreview}</blockquote>
                    ) : null}
                    {message.text || <em>[{message.kind}]</em>}
                    {message.fileId ? (
                      <span className="media-hint">Нажмите, чтобы открыть</span>
                    ) : null}
                    <time>{formatDate(message.createdAt)}</time>
                  </button>
                ))
              )}
              <div ref={bottom} />
            </div>
            {replyTo ? (
              <div className="reply-bar">
                <div>
                  <strong>Ответ на сообщение</strong>
                  <span>{replyTo.text || `[${replyTo.kind}]`}</span>
                </div>
                <button onClick={() => setReplyTo(null)}>
                  <X />
                </button>
              </div>
            ) : null}
            {attachment ? (
              <div className="reply-bar">
                <div>
                  <strong>Вложение</strong>
                  <span>{attachment.name}</span>
                </div>
                <button onClick={() => setAttachment(null)}>
                  <X />
                </button>
                <select
                  value={sendMediaKind}
                  onChange={(event) =>
                    setSendMediaKind(event.target.value as typeof sendMediaKind)
                  }
                  aria-label="Формат отправки"
                >
                  <option value="auto">Автоматически</option>
                  <option value="photo">Фото</option>
                  <option value="video">Видео</option>
                  <option value="voice">Голосовое</option>
                  <option value="video_note">Кружок</option>
                  <option value="document">Файл</option>
                </select>
              </div>
            ) : null}
            <form className="chat-composer" onSubmit={send}>
              <label className="icon-button file-button">
                <Paperclip />
                <input
                  type="file"
                  onChange={(event) =>
                    setAttachment(event.target.files?.[0] ?? null)
                  }
                />
              </label>
              <textarea
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Написать сообщение…"
                rows={1}
              />
              <Button
                type="submit"
                loading={sending}
                disabled={!text.trim() && !attachment}
              >
                <Send size={18} />
              </Button>
            </form>
            {mediaUrl ? (
              <Modal
                title="Вложение"
                onClose={() => {
                  URL.revokeObjectURL(mediaUrl);
                  setMediaUrl("");
                }}
              >
                <div className="media-viewer">
                  {mediaKind === "photo" ? (
                    <img src={mediaUrl} alt="Вложение" />
                  ) : mediaKind === "video" || mediaKind === "video_note" ? (
                    <video controls src={mediaUrl} />
                  ) : mediaKind === "voice" ? (
                    <audio controls src={mediaUrl} />
                  ) : (
                    <a
                      className="button button--primary"
                      href={mediaUrl}
                      download
                    >
                      Скачать файл
                    </a>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setMediaUrl("");
                    }}
                  >
                    Закрыть
                  </Button>
                </div>
              </Modal>
            ) : null}
          </>
        ) : (
          <EmptyState
            icon={<MessageSquare />}
            title="Выберите диалог"
            text="Здесь появится полная история переписки."
          />
        )}
      </section>
      {toast ? <Toast message={toast} onClose={() => setToast("")} /> : null}
    </div>
  );
}

export function ProposalsAdminPage() {
  const [status, setStatus] = useState("");
  const state = useAsync(() => api.adminProposals(status), [status]);
  const [editing, setEditing] = useState<Proposal | null>(null);
  const [revisions, setRevisions] = useState<ProposalRevision[]>([]);
  const [comment, setComment] = useState("");
  const [nextStatus, setNextStatus] = useState<Proposal["status"]>("received");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [attachmentLoading, setAttachmentLoading] = useState("");
  const [attachmentPreview, setAttachmentPreview] = useState<{
    url: string;
    name: string;
    mimeType: string;
  } | null>(null);
  const open = async (proposal: Proposal) => {
    try {
      setEditing(proposal);
      setComment(proposal.adminComment ?? "");
      setNextStatus(proposal.status);
      const [result] = await Promise.all([
        api.proposalRevisions(proposal.id),
        api.viewProposal(proposal.id),
      ]);
      setRevisions(result.revisions);
      if (!proposal.isViewed) await state.reload();
    } catch (caught) {
      setEditing(null);
      setToast(
        caught instanceof Error ? caught.message : "Не удалось открыть идею",
      );
    }
  };
  const openAttachment = async (
    id: string,
    name: string,
    mimeType: string,
  ) => {
    setAttachmentLoading(id);
    try {
      const blob = await api.attachmentFile(id);
      setAttachmentPreview({
        url: URL.createObjectURL(blob),
        name,
        mimeType: blob.type || mimeType,
      });
    } catch (caught) {
      setToast(
        caught instanceof Error ? caught.message : "Не удалось открыть вложение",
      );
    } finally {
      setAttachmentLoading("");
    }
  };
  const closeAttachment = () => {
    if (attachmentPreview) URL.revokeObjectURL(attachmentPreview.url);
    setAttachmentPreview(null);
  };
  const downloadPreview = () => {
    if (!attachmentPreview) return;
    const link = document.createElement("a");
    link.href = attachmentPreview.url;
    link.download = attachmentPreview.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const result = await api.updateProposalAdmin(editing.id, {
        status: nextStatus,
        comment,
      });
      setToast(
        result.notificationSent
          ? "Идея обновлена, автор уведомлён"
          : "Идея обновлена; сообщение автору доставить не удалось",
      );
      setEditing(null);
      await state.reload();
    } catch (caught) {
      setToast(
        caught instanceof Error ? caught.message : "Не удалось обновить идею",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="admin-page">
      <div className="admin-heading">
        <div>
          <p className="eyebrow">Предложка</p>
          <h1>Идеи</h1>
          <p>Рассматривайте предложения и отвечайте авторам.</p>
        </div>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">Все статусы</option>
          {Object.entries(PROPOSAL_STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {state.loading ? (
        <Spinner />
      ) : (
        <div className="admin-card-list">
          {state.data?.proposals.map((proposal) => (
            <Card key={proposal.id} className="admin-proposal">
              <div className="admin-proposal__user">
                <Avatar
                  name={proposal.user?.firstName ?? "?"}
                  src={proposal.user?.photoUrl}
                />
                <div>
                  <strong>
                    {proposal.user?.firstName} {proposal.user?.lastName}
                  </strong>
                  <span>
                    {formatDate(proposal.createdAt)} · версия {proposal.version}
                  </span>
                </div>
                <span className={`status status--${proposal.status}`}>
                  {PROPOSAL_STATUS_LABELS[proposal.status]}
                </span>
                {!proposal.isViewed ? (
                  <span className="new-badge">Новая</span>
                ) : null}
              </div>
              <h2>{proposal.title}</h2>
              <p>{proposal.description}</p>
              {proposal.links.map((link) => (
                <a key={link} href={link} target="_blank" rel="noreferrer">
                  {link}
                </a>
              ))}
              <Button variant="secondary" onClick={() => void open(proposal)}>
                Открыть и ответить
              </Button>
            </Card>
          ))}
        </div>
      )}
      {editing ? (
        <Modal title={editing.title} onClose={() => setEditing(null)}>
          <div className="modal-body">
            <p>{editing.description}</p>
            {editing.attachments.length ? (
              <div className="revision-files">
                <strong>Вложения</strong>
                {editing.attachments.map((attachment) => (
                  <button
                    type="button"
                    key={attachment.id}
                    onClick={() =>
                      void openAttachment(
                        attachment.id,
                        attachment.name,
                        attachment.mimeType,
                      )
                    }
                    disabled={attachmentLoading === attachment.id}
                  >
                    <Paperclip size={15} />
                    {attachmentLoading === attachment.id
                      ? "Открываем…"
                      : attachment.name}
                  </button>
                ))}
              </div>
            ) : null}
            {revisions.length ? (
              <details className="revision-history">
                <summary>Прошлые версии ({revisions.length})</summary>
                {revisions.map((revision) => (
                  <div key={revision.id} className="revision-item">
                    <strong>
                      Версия {revision.version} ·{" "}
                      {formatDate(revision.createdAt)}
                    </strong>
                    <h3>{revision.title}</h3>
                    <p>{revision.description}</p>
                    {revision.links.map((link) => (
                      <a
                        key={link}
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {link}
                      </a>
                    ))}
                    {revision.attachments.map((attachment) => (
                      <button
                        type="button"
                        key={attachment.id}
                        onClick={() =>
                          void openAttachment(
                            attachment.id,
                            attachment.name,
                            attachment.mimeType,
                          )
                        }
                        disabled={attachmentLoading === attachment.id}
                      >
                        <Paperclip size={14} />
                        {attachmentLoading === attachment.id
                          ? "Открываем…"
                          : attachment.name}
                      </button>
                    ))}
                  </div>
                ))}
              </details>
            ) : null}
            <label className="field">
              <span>Статус</span>
              <select
                value={nextStatus}
                onChange={(event) =>
                  setNextStatus(event.target.value as Proposal["status"])
                }
              >
                {Object.entries(PROPOSAL_STATUS_LABELS).map(([key, label]) => (
                  <option value={key} key={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Комментарий автору</span>
              <textarea
                rows={6}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Ответ появится в карточке и придёт сообщением…"
              />
            </label>
            <Button onClick={save} loading={saving}>
              Сохранить и уведомить
            </Button>
          </div>
        </Modal>
      ) : null}
      {attachmentPreview ? (
        <Modal title={attachmentPreview.name} onClose={closeAttachment}>
          <div className="media-viewer">
            {attachmentPreview.mimeType.startsWith("image/") ? (
              <img src={attachmentPreview.url} alt={attachmentPreview.name} />
            ) : attachmentPreview.mimeType.startsWith("video/") ? (
              <video controls src={attachmentPreview.url} />
            ) : attachmentPreview.mimeType.startsWith("audio/") ? (
              <audio controls src={attachmentPreview.url} />
            ) : (
              <p>Предпросмотр этого формата недоступен.</p>
            )}
            <div className="modal-actions">
              <Button onClick={downloadPreview}>Скачать файл</Button>
              <Button variant="secondary" onClick={closeAttachment}>
                Закрыть
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
      {toast ? <Toast message={toast} onClose={() => setToast("")} /> : null}
    </div>
  );
}

export function UsersAdminPage() {
  const [query, setQuery] = useState("");
  const state = useAsync(() => api.users(query), [query]);
  const tagsState = useAsync(() => api.tags(), []);
  const [toast, setToast] = useState("");
  const [tagUser, setTagUser] = useState<PublicUser | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [profile, setProfile] = useState<Awaited<
    ReturnType<typeof api.user>
  > | null>(null);
  const action = async (user: PublicUser, body: unknown, message: string) => {
    try {
      await api.updateUserState(user.id, body);
      setToast(message);
      await state.reload();
    } catch (caught) {
      setToast(caught instanceof Error ? caught.message : "Ошибка");
    }
  };
  const remove = async (user: PublicUser) => {
    if (
      !confirm(
        `Безвозвратно удалить ${user.firstName}, переписку, идеи и файлы?`,
      )
    )
      return;
    try {
      await api.deleteUser(user.id);
      setToast("Пользователь и его данные удалены");
      await state.reload();
    } catch (caught) {
      setToast(
        caught instanceof Error
          ? caught.message
          : "Не удалось удалить пользователя",
      );
    }
  };
  const openTags = async (user: PublicUser) => {
    const detail = await api.user(user.id);
    setTagUser(detail.user);
    setSelectedTags(detail.user.tags?.map((tag) => tag.id) ?? []);
  };
  const openProfile = async (user: PublicUser) =>
    setProfile(await api.user(user.id));
  const loadEarlierProfileMessages = async () => {
    if (
      !profile?.messagesHasMore ||
      !profile.messagesNextBeforeCreated ||
      !profile.messagesNextBeforeId
    )
      return;
    const earlier = await api.messages(
      profile.user.id,
      profile.messagesNextBeforeCreated,
      profile.messagesNextBeforeId,
    );
    setProfile({
      ...profile,
      messages: [...earlier.messages, ...profile.messages],
      messagesHasMore: earlier.hasMore,
      messagesNextBeforeCreated: earlier.nextBeforeCreated,
      messagesNextBeforeId: earlier.nextBeforeId,
    });
  };
  const createTag = async () => {
    if (!newTag.trim()) return;
    await api.createTag(newTag.trim(), "#596ff4");
    setNewTag("");
    await tagsState.reload();
  };
  const saveTags = async () => {
    if (!tagUser) return;
    await api.setUserTags(tagUser.id, selectedTags);
    setTagUser(null);
    setToast("Метки сохранены");
  };
  return (
    <div className="admin-page">
      <div className="admin-heading">
        <div>
          <p className="eyebrow">Аудитория</p>
          <h1>Пользователи</h1>
        </div>
        <label className="search search--wide">
          <Search />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Имя, username или ID"
          />
        </label>
      </div>
      {state.loading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Последняя активность</th>
                <th>Рассылки</th>
                <th>Состояние</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {state.data?.users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <button
                      className="table-user table-user--button"
                      onClick={() => void openProfile(user)}
                    >
                      <Avatar name={user.firstName} src={user.photoUrl} />
                      <div>
                        <strong>
                          {user.firstName} {user.lastName}
                        </strong>
                        <span>
                          {user.username
                            ? `@${user.username}`
                            : `ID ${user.id}`}
                        </span>
                      </div>
                    </button>
                  </td>
                  <td>{formatDate(user.lastSeenAt)}</td>
                  <td>{user.broadcastEnabled ? "Включены" : "Отключены"}</td>
                  <td>
                    <span className={`dot dot--${user.state}`} />
                    {user.state === "active"
                      ? "Активен"
                      : user.state === "blocked"
                        ? "Заблокирован"
                        : "Недоступен"}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button title="Метки" onClick={() => openTags(user)}>
                        <Plus />
                      </button>
                      <button
                        title="Архив"
                        onClick={() =>
                          action(
                            user,
                            { archived: !user.archived },
                            user.archived
                              ? "Возвращён из архива"
                              : "Перемещён в архив",
                          )
                        }
                      >
                        <Archive />
                      </button>
                      <button
                        title="Тишина"
                        onClick={() =>
                          action(
                            user,
                            { muted: !user.muted },
                            user.muted
                              ? "Уведомления включены"
                              : "Уведомления отключены",
                          )
                        }
                      >
                        <BellOff />
                      </button>
                      <button
                        title="Блокировка"
                        onClick={() =>
                          action(
                            user,
                            { blocked: user.state !== "blocked" },
                            "Состояние обновлено",
                          )
                        }
                      >
                        <Ban />
                      </button>
                      <button
                        className="danger"
                        title="Удалить"
                        onClick={() => remove(user)}
                      >
                        <Trash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {profile ? (
        <Modal
          title={`${profile.user.firstName} ${profile.user.lastName ?? ""}`}
          onClose={() => setProfile(null)}
        >
          <div className="modal-body user-profile-detail">
            <div className="profile-summary">
              <Avatar
                name={profile.user.firstName}
                src={profile.user.photoUrl}
                size={64}
              />
              <div>
                <strong>
                  {profile.user.username
                    ? `@${profile.user.username}`
                    : `Telegram ID ${profile.user.id}`}
                </strong>
                <span>
                  Первый контакт: {formatDate(profile.user.firstSeenAt)}
                </span>
                <span>
                  Последний контакт: {formatDate(profile.user.lastSeenAt)}
                </span>
              </div>
            </div>
            <div className="profile-metrics">
              <span>
                Идей <b>{profile.proposals.length}</b>
              </span>
              <span>
                Сообщений <b>{profile.messageCount}</b>
              </span>
              <span>
                Рассылок <b>{profile.deliveries.length}</b>
              </span>
            </div>
            {profile.user.tags?.length ? (
              <div className="profile-tags">
                {profile.user.tags.map((tag) => (
                  <span
                    key={tag.id}
                    style={{ "--tag-color": tag.color } as CSSProperties}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            ) : null}
            {profile.proposals.length ? (
              <details className="profile-section">
                <summary>Идеи ({profile.proposals.length})</summary>
                {profile.proposals.map((proposal) => (
                  <p key={proposal.id}>
                    {PROPOSAL_STATUS_LABELS[proposal.status]} · {proposal.title}
                  </p>
                ))}
              </details>
            ) : null}
            {profile.messages.length ? (
              <details className="profile-section">
                <summary>Вся переписка ({profile.messageCount})</summary>
                <div className="profile-messages">
                  {profile.messagesHasMore ? (
                    <Button
                      variant="secondary"
                      onClick={loadEarlierProfileMessages}
                    >
                      Показать более ранние сообщения
                    </Button>
                  ) : null}
                  {profile.messages.map((message) => (
                    <div key={message.id} className={message.direction}>
                      <strong>
                        {message.direction === "inbound"
                          ? profile.user.firstName
                          : "Вы"}
                      </strong>
                      <p>{message.text || `[${message.kind}]`}</p>
                      <small>{formatDate(message.createdAt)}</small>
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
            {profile.deliveries.length ? (
              <details className="profile-section">
                <summary>
                  История рассылок ({profile.deliveries.length})
                </summary>
                {profile.deliveries.map((delivery) => (
                  <p key={`${delivery.broadcast_id}-${delivery.created_at}`}>
                    {delivery.title} · {delivery.status}
                    {delivery.error ? ` · ${delivery.error}` : ""}
                  </p>
                ))}
              </details>
            ) : null}
            <a
              className="button button--secondary"
              href={
                profile.user.username
                  ? `https://t.me/${profile.user.username}`
                  : `tg://user?id=${profile.user.id}`
              }
            >
              <ExternalLink size={17} />
              Открыть в Telegram
            </a>
          </div>
        </Modal>
      ) : null}
      {tagUser ? (
        <Modal
          title={`Метки: ${tagUser.firstName}`}
          onClose={() => setTagUser(null)}
        >
          <div className="modal-body">
            <div className="tag-picker">
              {tagsState.data?.tags.map((tag) => (
                <label
                  key={tag.id}
                  style={{ "--tag-color": tag.color } as CSSProperties}
                >
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag.id)}
                    onChange={() =>
                      setSelectedTags(
                        selectedTags.includes(tag.id)
                          ? selectedTags.filter((id) => id !== tag.id)
                          : [...selectedTags, tag.id],
                      )
                    }
                  />
                  <span>{tag.name}</span>
                  <small>{tag.user_count}</small>
                </label>
              ))}
            </div>
            <div className="inline-form">
              <input
                value={newTag}
                onChange={(event) => setNewTag(event.target.value)}
                placeholder="Новая метка"
              />
              <Button variant="secondary" onClick={createTag}>
                Создать
              </Button>
            </div>
            <Button onClick={saveTags}>Сохранить метки</Button>
          </div>
        </Modal>
      ) : null}
      {toast ? <Toast message={toast} onClose={() => setToast("")} /> : null}
    </div>
  );
}

export function BroadcastsAdminPage() {
  const state = useAsync(() => api.broadcasts(), []);
  const tagsState = useAsync(() => api.tags(), []);
  const [compose, setCompose] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [schedule, setSchedule] = useState("");
  const [buttons, setButtons] = useState<Array<{ label: string; url: string }>>(
    [],
  );
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [activeDays, setActiveDays] = useState(0);
  const [proposalStatuses, setProposalStatuses] = useState<
    Proposal["status"][]
  >([]);
  const [testOnlyAdmin, setTestOnlyAdmin] = useState(false);
  const [media, setMedia] = useState<File | null>(null);
  const [mediaKind, setMediaKind] = useState<
    "auto" | "photo" | "video" | "voice" | "video_note" | "document"
  >("auto");
  const [deliveries, setDeliveries] = useState<{
    campaign: Broadcast;
    rows: BroadcastDelivery[];
  } | null>(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");
  const [draftReady, setDraftReady] = useState(false);
  useDebouncedEffect(
    () => {
      if (!compose || !draftReady) return;
      void (title ||
      text ||
      schedule ||
      buttons.length ||
      selectedTags.length ||
      activeDays ||
      proposalStatuses.length ||
      testOnlyAdmin
        ? api.saveDraft("broadcast", "new", {
            title,
            text,
            schedule,
            buttons,
            selectedTags,
            activeDays,
            proposalStatuses,
            testOnlyAdmin,
          })
        : api.clearDraft("broadcast", "new"));
    },
    800,
    [
      compose,
      title,
      text,
      schedule,
      buttons,
      selectedTags,
      activeDays,
      proposalStatuses,
      testOnlyAdmin,
      draftReady,
    ],
  );
  useEffect(() => {
    void api.loadDraft("broadcast", "new").then((result) => {
      const draft = result.draft;
      if (draft) {
        setTitle(String(draft.title ?? ""));
        setText(String(draft.text ?? ""));
        setSchedule(String(draft.schedule ?? ""));
        setButtons(
          Array.isArray(draft.buttons)
            ? (draft.buttons as Array<{ label: string; url: string }>)
            : [],
        );
        setSelectedTags(
          Array.isArray(draft.selectedTags)
            ? draft.selectedTags.map(String)
            : [],
        );
        setActiveDays(Number(draft.activeDays ?? 0));
        setProposalStatuses(
          Array.isArray(draft.proposalStatuses)
            ? (draft.proposalStatuses as Proposal["status"][])
            : [],
        );
        setTestOnlyAdmin(Boolean(draft.testOnlyAdmin));
      }
      setDraftReady(true);
    });
  }, []);
  const create = async (event: FormEvent, draft = false) => {
    event.preventDefault();
    setSending(true);
    try {
      const scheduledAt = schedule ? saratovLocalToIso(schedule) : undefined;
      const attachment = media ? await api.upload(media) : null;
      const inferredKind = media
        ? media.type.startsWith("image/")
          ? "photo"
          : media.type.startsWith("video/")
            ? "video"
            : media.type.startsWith("audio/")
              ? "voice"
              : "document"
        : "text";
      const kind = media && mediaKind !== "auto" ? mediaKind : inferredKind;
      const filter = {
        testOnlyAdmin: testOnlyAdmin || undefined,
        tagIds:
          !testOnlyAdmin && selectedTags.length ? selectedTags : undefined,
        activeSince:
          !testOnlyAdmin && activeDays
            ? new Date(Date.now() - activeDays * 86400000).toISOString()
            : undefined,
        proposalStatuses:
          !testOnlyAdmin && proposalStatuses.length
            ? proposalStatuses
            : undefined,
      };
      const result = await api.createBroadcast({
        title,
        content: {
          kind,
          text: kind === "text" ? text : undefined,
          caption: kind !== "text" ? text : undefined,
          attachmentId: attachment?.id,
          buttons,
        },
        filter,
        scheduledAt,
        saveAsDraft: draft,
      });
      setToast(
        draft
          ? "Черновик сохранён"
          : `Рассылка создана: ${result.recipientTotal} получателей`,
      );
      setCompose(false);
      setTitle("");
      setText("");
      setSchedule("");
      setButtons([]);
      setMedia(null);
      setSelectedTags([]);
      setActiveDays(0);
      setProposalStatuses([]);
      setTestOnlyAdmin(false);
      await api.clearDraft("broadcast", "new");
      await state.reload();
    } catch (caught) {
      setToast(caught instanceof Error ? caught.message : "Ошибка");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="admin-page">
      <div className="admin-heading">
        <div>
          <p className="eyebrow">Коммуникации</p>
          <h1>Рассылки</h1>
          <p>Отправка идёт безопасными порциями по 40 получателей в минуту.</p>
        </div>
        <Button onClick={() => setCompose(true)}>
          <Plus />
          Новая рассылка
        </Button>
      </div>
      {state.loading ? (
        <Spinner />
      ) : !state.data?.broadcasts.length ? (
        <EmptyState
          icon={<Radio />}
          title="Рассылок ещё нет"
          text="Создайте первое массовое сообщение или отправьте голосовое напрямую боту."
        />
      ) : (
        <div className="broadcast-list">
          {state.data.broadcasts.map((campaign) => (
            <BroadcastRow
              key={campaign.id}
              campaign={campaign}
              onDetails={async () => {
                const result = await api.broadcastDeliveries(campaign.id);
                setDeliveries({ campaign, rows: result.deliveries });
              }}
              onStart={async () => {
                await api.startBroadcast(campaign.id);
                await state.reload();
              }}
              onCancel={async () => {
                await api.cancelBroadcast(campaign.id);
                await state.reload();
              }}
            />
          ))}
        </div>
      )}
      {compose ? (
        <Modal title="Новая рассылка" onClose={() => setCompose(false)}>
          <form className="modal-body" onSubmit={(event) => create(event)}>
            <label className="field">
              <span>Название для себя</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                required
                placeholder="Например: Новое видео"
              />
            </label>
            <label className="field">
              <span>Текст или подпись</span>
              <textarea
                rows={8}
                value={text}
                onChange={(event) => setText(event.target.value)}
                required
                maxLength={media ? 1024 : 4000}
                placeholder="Сообщение получателям…"
              />
            </label>
            <label className="field">
              <span>
                Медиа <small>необязательно, до 20 МБ</small>
              </span>
              <input
                type="file"
                accept="image/*,video/*,audio/*,.pdf,.zip"
                onChange={(event) => setMedia(event.target.files?.[0] ?? null)}
              />
            </label>
            {media ? (
              <label className="field">
                <span>Как отправить файл</span>
                <select
                  value={mediaKind}
                  onChange={(event) =>
                    setMediaKind(event.target.value as typeof mediaKind)
                  }
                >
                  <option value="auto">Определить автоматически</option>
                  <option value="photo">Фото</option>
                  <option value="video">Видео</option>
                  <option value="voice">Голосовое</option>
                  <option value="video_note">Кружок</option>
                  <option value="document">Файл</option>
                </select>
              </label>
            ) : null}
            <label className="field">
              <span>
                Отправить позже <small>саратовское время</small>
              </span>
              <input
                type="datetime-local"
                value={schedule}
                onChange={(event) => setSchedule(event.target.value)}
              />
            </label>
            <label className="setting-row compact-setting">
              <div>
                <strong>Тест только себе</strong>
                <p>Никто кроме владельца не получит сообщение.</p>
              </div>
              <button
                type="button"
                className={`switch ${testOnlyAdmin ? "switch--on" : ""}`}
                onClick={() => setTestOnlyAdmin(!testOnlyAdmin)}
              >
                <span />
              </button>
            </label>
            {!testOnlyAdmin ? (
              <>
                <div className="field">
                  <span>Получатели по меткам</span>
                  <div className="chip-picker">
                    {tagsState.data?.tags.map((tag) => (
                      <button
                        type="button"
                        className={
                          selectedTags.includes(tag.id) ? "active" : ""
                        }
                        key={tag.id}
                        onClick={() =>
                          setSelectedTags(
                            selectedTags.includes(tag.id)
                              ? selectedTags.filter((id) => id !== tag.id)
                              : [...selectedTags, tag.id],
                          )
                        }
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="segment-grid">
                  <label className="field">
                    <span>Активность</span>
                    <select
                      value={activeDays}
                      onChange={(event) =>
                        setActiveDays(Number(event.target.value))
                      }
                    >
                      <option value="0">Любая</option>
                      <option value="7">За 7 дней</option>
                      <option value="30">За 30 дней</option>
                      <option value="90">За 90 дней</option>
                    </select>
                  </label>
                  <div className="field">
                    <span>Статус идей</span>
                    <div className="chip-picker">
                      {Object.entries(PROPOSAL_STATUS_LABELS).map(
                        ([key, label]) => (
                          <button
                            type="button"
                            className={
                              proposalStatuses.includes(
                                key as Proposal["status"],
                              )
                                ? "active"
                                : ""
                            }
                            key={key}
                            onClick={() =>
                              setProposalStatuses(
                                proposalStatuses.includes(
                                  key as Proposal["status"],
                                )
                                  ? proposalStatuses.filter(
                                      (value) => value !== key,
                                    )
                                  : [
                                      ...proposalStatuses,
                                      key as Proposal["status"],
                                    ],
                              )
                            }
                          >
                            {label}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            <div className="field">
              <span>Кнопки со ссылками</span>
              {buttons.map((button, index) => (
                <div className="button-fields" key={index}>
                  <input
                    placeholder="Текст"
                    value={button.label}
                    onChange={(event) =>
                      setButtons(
                        buttons.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, label: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                  <input
                    placeholder="https://…"
                    value={button.url}
                    onChange={(event) =>
                      setButtons(
                        buttons.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, url: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setButtons(
                        buttons.filter((_, itemIndex) => itemIndex !== index),
                      )
                    }
                  >
                    <X />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                onClick={() => setButtons([...buttons, { label: "", url: "" }])}
              >
                + Добавить кнопку
              </Button>
            </div>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={(event) => create(event as unknown as FormEvent, true)}
                loading={sending}
              >
                Сохранить черновик
              </Button>
              <Button type="submit" loading={sending}>
                {schedule ? "Запланировать" : "Запустить"}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
      {deliveries ? (
        <Modal
          title={`Получатели: ${deliveries.campaign.title}`}
          onClose={() => setDeliveries(null)}
        >
          <div className="modal-body delivery-list">
            {!deliveries.rows.length ? (
              <p>Получатели ещё не сформированы.</p>
            ) : (
              deliveries.rows.map((delivery) => (
                <div key={delivery.id}>
                  <Avatar
                    name={delivery.user.firstName}
                    src={delivery.user.photoUrl}
                    size={34}
                  />
                  <div>
                    <strong>
                      {delivery.user.firstName} {delivery.user.lastName}
                    </strong>
                    <span>
                      {delivery.user.username
                        ? `@${delivery.user.username}`
                        : `ID ${delivery.user.id}`}
                    </span>
                  </div>
                  <div>
                    <b>{delivery.status}</b>
                    {delivery.error ? (
                      <small>{delivery.error}</small>
                    ) : delivery.sentAt ? (
                      <small>{formatDate(delivery.sentAt)}</small>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </Modal>
      ) : null}
      {toast ? <Toast message={toast} onClose={() => setToast("")} /> : null}
    </div>
  );
}

function BroadcastRow({
  campaign,
  onDetails,
  onStart,
  onCancel,
}: {
  campaign: Broadcast;
  onDetails(): Promise<void>;
  onStart(): Promise<void>;
  onCancel(): Promise<void>;
}) {
  const percent = campaign.recipientTotal
    ? Math.round(
        ((campaign.sentCount + campaign.failedCount + campaign.optedOutCount) /
          campaign.recipientTotal) *
          100,
      )
    : 0;
  const labels: Record<Broadcast["status"], string> = {
    draft: "Черновик",
    scheduled: "Запланирована",
    queued: "В очереди",
    sending: "Отправляется",
    completed: "Завершена",
    cancelled: "Отменена",
    failed: "Ошибка",
  };
  return (
    <Card className="broadcast-row">
      <div className="broadcast-row__top">
        <div>
          <strong>{campaign.title}</strong>
          <span>{formatDate(campaign.createdAt)}</span>
        </div>
        <span className={`campaign-status campaign-status--${campaign.status}`}>
          {labels[campaign.status]}
        </span>
      </div>
      <p>
        {campaign.content.text ||
          campaign.content.caption ||
          `[${campaign.content.kind}]`}
      </p>
      <div className="progress">
        <span style={{ width: `${percent}%` }} />
      </div>
      <div className="campaign-metrics">
        <span>
          Получатели <b>{campaign.recipientTotal}</b>
        </span>
        <span>
          Ожидает <b>{campaign.pendingCount}</b>
        </span>
        <span>
          Отправлено <b>{campaign.sentCount}</b>
        </span>
        <span>
          Ошибки <b>{campaign.failedCount}</b>
        </span>
        <span>
          Отказались <b>{campaign.optedOutCount}</b>
        </span>
        <span>
          Переходы <b>{campaign.clickCount}</b>
        </span>
      </div>
      <Button variant="secondary" onClick={onDetails}>
        Получатели и ошибки
      </Button>
      {campaign.status === "draft" ? (
        <Button onClick={onStart}>Запустить черновик</Button>
      ) : null}
      {["draft", "scheduled", "queued", "sending"].includes(campaign.status) ? (
        <Button variant="ghost" onClick={onCancel}>
          Отменить
        </Button>
      ) : null}
    </Card>
  );
}
